import type { ArticleContent } from './types';

const fr: Record<string, ArticleContent> = {
  'allchinabuy-spreadsheet': {
    title: 'AllChinaBuy Spreadsheet : guide complet produits et vendeurs',
    shortDescription: 'Le guide complet du tableur AllChinaBuy pour trouver produits, vendeurs et ressources shopping au même endroit.',
    metaTitle: 'AllChinaBuy Spreadsheet : guide complet produits et vendeurs',
    metaDescription: 'Découvrez ce que sont les tableurs AllChinaBuy, comment les utiliser pour trouver produits et vendeurs, et organiser vos recherches avec ce guide pour débutants.',
    h1: 'AllChinaBuy Spreadsheet : guide complet produits et vendeurs',
    intro: 'Le tableur AllChinaBuy est une façon pratique de garder les informations produits, vendeurs et shopping organisées dans une seule liste facile à parcourir. Que vous découvriez les agents d achat ou que vous cherchiez une méthode plus structurée, un tableur bien organisé vous aide à comparer les options et à décider sereinement.',
    sections: [
      {
        heading: 'Qu est-ce qu un tableur AllChinaBuy ?',
        paragraphs: [
          'Un tableur AllChinaBuy est simplement une liste structurée pour rassembler et comparer les informations produits et vendeurs. Ce n est pas un outil officiel, mais une habitude courante chez les acheteurs pour garder une trace des liens, tailles, prix, notes d entrepôt et détails QC.',
          'Parce qu un achat via agent implique souvent des dizaines de liens, un tableur transforme des informations dispersées en lignes et colonnes faciles à scanner.',
        ],
        list: [
          'Noms des produits et liens directs',
          'Tailles, couleurs et variantes',
          'Prix estimés et quantités',
          'Noms et notes des vendeurs',
          'Notes d entrepôt et QC',
        ],
      },
      {
        heading: 'Pourquoi utiliser un tableur pour vos achats ?',
        paragraphs: [
          'Le principal avantage est la clarté. Au lieu de garder vos liens dans l historique ou une application de notes, un tableur donne à chaque article une place prévisible, triable par catégorie.',
          'Le second avantage est la comparaison : placer côte à côte les détails de deux vendeurs facilite la pesée du prix, de la livraison et des avis.',
        ],
      },
      {
        heading: 'Comment construire votre propre tableur',
        paragraphs: [
          'Vous n avez besoin d aucun logiciel spécial. Commencez par les colonnes qui comptent pour vous, puis ajoutez-en au fil de votre flux de travail.',
          'Une disposition pratique inclut une colonne article, une colonne lien, une colonne prix, une colonne vendeur et une colonne statut.',
        ],
        list: [
          'Créez des colonnes article, lien, prix, vendeur et statut',
          'Ajoutez une ligne par produit envisagé',
          'Mettez à jour le statut au fil des étapes',
          'Gardez une feuille séparée pour les commandes terminées',
        ],
      },
      {
        heading: 'Les champs courants expliqués',
        paragraphs: [
          'Le lien produit est le champ le plus important car il conserve l article exact trouvé. Le nom du vendeur aide à se souvenir, le prix à suivre les totaux, et les champs entrepôt/QC à suivre l article après réception.',
          'Gardez aussi une colonne notes pour tout détail inhabituel, comme une recommandation de taille ou un rappel de vérifier les mesures.',
        ],
      },
    ],
    comparison: {
      title: 'Champ du tableur et son utilité',
      columns: ['Champ', 'Ce qu il suit', 'Pourquoi c est utile'],
      rows: [
        ['Lien produit', 'URL exacte de l article', 'Retrouve le même produit plus tard'],
        ['Vendeur', 'Qui propose l article', 'Compare les avis vendeurs'],
        ['Prix', 'Coût estimé', 'Suit le total avant commande'],
        ['Statut', 'Étape de commande', 'Montre ce qui reste à faire'],
        ['Note QC', 'Résultat de l inspection', 'Confirme la conformité'],
      ],
    },
    faqs: [
      { q: 'Qu est-ce qu un tableur AllChinaBuy ?', a: 'Une liste structurée pour organiser liens produits, vendeurs, prix et notes de livraison ou QC au même endroit.' },
      { q: 'Le tableur est-il une fonctionnalité officielle ?', a: 'Non. C est une convention d acheteurs pour rester organisé, pas un produit officiel AllChinaBuy.' },
      { q: 'Faut-il un logiciel spécial ?', a: 'Non. Un outil de tableur basique suffit, avec des colonnes simples pour article, lien, prix, vendeur et statut.' },
      { q: 'Que mettre dans chaque ligne ?', a: 'Au minimum le lien, le vendeur, le prix estimé et le statut, puis taille, couleur, entrepôt et QC au besoin.' },
    ],
  },

  'allchinabuy-review': {
    title: 'Avis AllChinaBuy : est-ce fiable et sûr ?',
    shortDescription: 'Un regard indépendant et honnête sur AllChinaBuy : ce que c est, comment ça marche et quoi vérifier avant d acheter.',
    metaTitle: 'Avis AllChinaBuy : est-ce fiable et sûr ?',
    metaDescription: 'Un avis indépendant sur AllChinaBuy : comment fonctionne l agent d achat, à quoi s attendre pour la commande et la livraison, et des conseils pour acheter plus sereinement.',
    h1: 'Avis AllChinaBuy : est-ce fiable et sûr ?',
    intro: 'AllChinaBuy est un agent d achat qui aide les acheteurs internationaux à commander des produits sur les marketplaces chinoises et à les faire expédier à l étranger. Cet avis indépendant explique le fonctionnement général, le déroulement type et les points à vérifier avant votre première commande.',
    sections: [
      {
        heading: 'Comment fonctionne un agent d achat',
        paragraphs: [
          'Un agent d achat sert d intermédiaire entre vous et les vendeurs. Vous partagez un lien produit, et l agent achète, stocke puis expédie l article vers votre pays.',
          'La plupart des agents, dont AllChinaBuy, suivent un flux similaire : ajout, achat, réception en entrepôt, puis choix d une méthode d expédition.',
        ],
      },
      {
        heading: 'À quoi ça sert généralement',
        paragraphs: [
          'On utilise souvent un agent pour la mode, les chaussures, le streetwear et les accessoires difficiles à trouver localement. L agent aide aussi à regrouper plusieurs commandes en un seul colis.',
        ],
        list: [
          'Mode et streetwear',
          'Chaussures et sneakers',
          'Accessoires et sacs',
          'Regrouper plusieurs commandes en un envoi',
        ],
      },
      {
        heading: 'À vérifier avant de commander',
        paragraphs: [
          'Comme l agent est un prestataire et non une garantie de marketplace, vérifiez les frais, les options de livraison et les règles de stockage avant de vous engager.',
          'Consulter les retours récents d autres acheteurs aide aussi à fixer des attentes réalistes sur les délais et le support.',
        ],
      },
      {
        heading: 'Un regard équilibré',
        paragraphs: [
          'Un agent peut vous éviter de traiter directement avec plusieurs vendeurs et langues, mais il ajoute une étape et des frais. Pour beaucoup, la commodité en vaut la peine ; pour d autres, comparer quelques agents d abord est plus judicieux.',
        ],
      },
    ],
    comparison: {
      title: 'Agent vs. achat direct',
      columns: ['Facteur', 'Via un agent', 'Direct vendeur'],
      rows: [
        ['Expédition internationale', 'L agent regroupe et expédie', 'Souvent indisponible'],
        ['Commandes multiples', 'Regroupées en un colis', 'Chaque vendeur expédie séparément'],
        ['Barrière linguistique', 'L agent gère la communication', 'Vous contactez le vendeur'],
        ['Coût supplémentaire', 'Frais de service', 'Généralement moins cher'],
        ['Photos QC', 'Souvent disponibles', 'Rarement proposées'],
      ],
    },
    faqs: [
      { q: 'AllChinaBuy est-il fiable ?', a: 'C est un vrai service d agent d achat. Comme pour tout service, vérifiez les conditions, les frais et les retours récents avant de commander.' },
      { q: 'Comment fonctionne AllChinaBuy ?', a: 'Vous partagez un lien, l agent achète et stocke, puis vous choisissez une méthode d expédition internationale.' },
      { q: 'Un agent coûte-t-il plus cher ?', a: 'Des frais de service s appliquent, mais le regroupement et l accès à l international restent pratiques. Comparez le coût total.' },
      { q: 'Puis-je obtenir des photos QC ?', a: 'Beaucoup d agents proposent des photos QC. Confirmez la disponibilité et les éventuels frais.' },
    ],
  },

  'allchinabuy-shipping-cost': {
    title: 'AllChinaBuy : coût de livraison, guide complet',
    shortDescription: 'Comprenez ce qui influence le coût de livraison AllChinaBuy et comment estimer votre total avant de commander.',
    metaTitle: 'AllChinaBuy : coût de livraison, guide complet',
    metaDescription: 'Un guide clair sur les frais de livraison AllChinaBuy : facteurs de prix, poids volumétrique et astuces pour réduire la facture.',
    h1: 'AllChinaBuy : coût de livraison, guide complet',
    intro: 'Le coût de livraison est souvent la plus grande surprise des premiers acheteurs. Ce guide explique les principaux facteurs de prix, le calcul du poids et du volume, et des moyens simples de garder un total raisonnable.',
    sections: [
      {
        heading: 'Ce qui influence le coût',
        paragraphs: [
          'Plusieurs facteurs déterminent le prix. Les deux plus importants sont le poids du colis et la destination. La méthode, les dimensions et les services supplémentaires jouent aussi.',
          'Comme les tarifs évoluent, vérifiez toujours les options actuelles de votre compte plutôt que d anciens devis.',
        ],
        list: [
          'Poids et taille du colis',
          'Pays de destination',
          'Méthode d expédition choisie',
          'Assurance et services supplémentaires',
          'Règles de douane',
        ],
      },
      {
        heading: 'Poids réel vs. poids volumétrique',
        paragraphs: [
          'Les transporteurs facturent le poids réel ou volumétrique, selon le plus élevé. Le poids volumétrique est calculé à partir des dimensions, donc un article grand mais léger peut coûter plus cher.',
          'C est pourquoi des colis compacts et bien emballés sont souvent moins chers à expédier.',
        ],
      },
      {
        heading: 'Comment estimer votre total',
        paragraphs: [
          'Commencez par le poids estimé de vos articles, puis comparez les lignes disponibles pour votre destination. La plupart des agents montrent une estimation en direct une fois le colis pesé.',
          'N oubliez pas la livraison domestique vers l entrepôt, les frais de service et les éventuels frais de douane.',
        ],
      },
      {
        heading: 'Moyens pratiques de réduire le coût',
        paragraphs: [
          'Regrouper plusieurs articles en un seul colis est le moyen le plus efficace d économiser. Choisir une ligne plus lente et retirer les emballages volumineux fait aussi la différence.',
        ],
        list: [
          'Consolidez plusieurs articles en un colis',
          'Retirez les emballages volumineux',
          'Comparez les lignes et vitesses',
          'Expédiez les articles lourds ensemble quand c est pertinent',
        ],
      },
    ],
    comparison: {
      title: 'Facteur de livraison et ce qu il faut vérifier',
      columns: ['Facteur', 'À vérifier', 'Pourquoi c est important'],
      rows: [
        ['Poids', 'Réel vs. volumétrique', 'Détermine le poids facturé'],
        ['Destination', 'Votre pays', 'Change les lignes et le prix'],
        ['Méthode', 'Options disponibles', 'Vitesse et coût varient'],
        ['Assurance', 'Conditions actuelles', 'Protège les commandes chères'],
        ['Douane', 'Règles locales', 'Évite les frais surprises'],
      ],
    },
    faqs: [
      { q: 'Combien coûte la livraison AllChinaBuy ?', a: 'Cela dépend du poids, de la taille, de la destination et de la ligne choisie. Consultez l estimation en direct.' },
      { q: 'Qu est-ce que le poids volumétrique ?', a: 'Un poids facturé calculé à partir des dimensions. Le transporteur facture le réel ou le volumétrique, le plus élevé.' },
      { q: 'Comment réduire les frais ?', a: 'Consolidez les articles, retirez les emballages volumineux et comparez les lignes et vitesses.' },
      { q: 'Les frais de douane sont-ils inclus ?', a: 'Généralement non. Vérifiez les règles de votre pays pour éviter les surprises.' },
    ],
  },

  'allchinabuy-shipping': {
    title: 'AllChinaBuy : guide d expédition, méthodes et délais',
    shortDescription: 'Comment fonctionne l expédition AllChinaBuy, les méthodes courantes et des astuces pour une livraison fluide.',
    metaTitle: 'AllChinaBuy : guide d expédition, méthodes et délais',
    metaDescription: 'Un guide pratique de l expédition AllChinaBuy : processus, méthodes courantes et astuces pour une livraison sans accroc.',
    h1: 'AllChinaBuy : guide d expédition, méthodes et délais',
    intro: 'Comprendre l expédition avec un agent rend toute l expérience plus fluide. Ce guide parcourt le processus de l entrepôt à votre porte, explique les méthodes courantes et partage des astuces pour éviter les retards.',
    sections: [
      {
        heading: 'Le processus étape par étape',
        paragraphs: [
          'Une fois vos articles arrivés à l entrepôt, ils sont pesés et photographiés. Vous choisissez ensuite une méthode, payez les frais, et le colis est expédié.',
          'Vous recevez généralement un numéro de suivi. Le délai dépend de la méthode et de votre destination.',
        ],
        list: [
          'Les articles arrivent et sont pesés à l entrepôt',
          'Vous choisissez une ligne et confirmez',
          'Le colis est expédié avec un numéro de suivi',
          'Vous le suivez jusqu à la livraison',
        ],
      },
      {
        heading: 'Méthodes d expédition courantes',
        paragraphs: [
          'Les lignes se répartissent en express, standard et économique. L express est plus rapide mais plus cher, l économique moins cher mais plus lent.',
          'Certaines lignes conviennent mieux à certaines destinations ou tailles, donc comparez les options pour votre colis.',
        ],
      },
      {
        heading: 'Suivi et assurance',
        paragraphs: [
          'La plupart des méthodes incluent un suivi. L assurance est souvent optionnelle mais utile pour les commandes de valeur.',
        ],
      },
      {
        heading: 'Astuces pour une livraison fluide',
        paragraphs: [
          'Vérifiez votre adresse avant de confirmer et suivez le colis pour répondre rapidement aux avis de douane. La consolidation réduit aussi coût et nombre de livraisons.',
        ],
        list: [
          'Vérifiez soigneusement votre adresse',
          'Envisagez l assurance pour les objets de valeur',
          'Suivez régulièrement votre colis',
          'Consolidez les commandes pour économiser',
        ],
      },
    ],
    comparison: {
      title: 'Méthode et à quoi s attendre',
      columns: ['Méthode', 'Vitesse type', 'Coût'],
      rows: [
        ['Express', 'Plus rapide', 'Plus élevé'],
        ['Standard', 'Équilibré', 'Modéré'],
        ['Économique', 'Plus lent', 'Plus bas'],
      ],
    },
    faqs: [
      { q: 'Combien de temps prend l expédition ?', a: 'Le délai dépend de la méthode et de la destination. L express est plus rapide, l économique plus lent.' },
      { q: 'Puis-je suivre mon colis ?', a: 'Oui, la plupart des méthodes incluent un numéro de suivi.' },
      { q: 'Faut-il prendre une assurance ?', a: 'Optionnelle mais recommandée pour les commandes de valeur.' },
      { q: 'Puis-je expédier plusieurs commandes ensemble ?', a: 'Oui, la consolidation réduit le nombre d envois et le coût total.' },
    ],
  },

  'allchinabuy-coupon': {
    title: 'AllChinaBuy : guide coupons et codes promo',
    shortDescription: 'Comment trouver et utiliser les coupons AllChinaBuy, et des astuces pour éviter les fausses offres.',
    metaTitle: 'AllChinaBuy : guide coupons et codes promo',
    metaDescription: 'Découvrez comment fonctionnent les coupons et codes AllChinaBuy, où les chercher et comment repérer les fausses offres.',
    h1: 'AllChinaBuy : guide coupons et codes promo',
    intro: 'Les coupons et codes promo peuvent rendre l achat via agent un peu moins cher, à condition de comprendre leur fonctionnement. Ce guide explique d où viennent les offres, comment les appliquer et comment éviter les codes faux ou expirés.',
    sections: [
      {
        heading: 'D où viennent les réductions',
        paragraphs: [
          'Les réductions apparaissent souvent sous forme de campagnes promotionnelles, offres saisonnières ou codes partagés sur les canaux officiels du service.',
          'Comme les offres changent fréquemment, le plus fiable est de consulter les annonces officielles plutôt que des sites tiers.',
        ],
      },
      {
        heading: 'Comment appliquer un code',
        paragraphs: [
          'Les codes se saisissent généralement au paiement ou s appliquent à une partie de la commande. Lisez les conditions : beaucoup ont un minimum d achat, une date d expiration ou une restriction de catégorie.',
          'Si un code ne fonctionne pas, vérifiez l orthographe, la date et les conditions.',
        ],
      },
      {
        heading: 'Comment éviter les faux coupons',
        paragraphs: [
          'Méfiez-vous des sites promettant des réductions énormes ou illimitées. Les offres légitimes sont modestes et clairement liées à des conditions.',
          'Ne partagez jamais vos informations de paiement sur un site tiers en échange d un code.',
        ],
      },
      {
        heading: 'Autres façons d économiser',
        paragraphs: [
          'Au-delà des codes, la consolidation des commandes et le bon choix de ligne d expédition économisent souvent plus qu une petite remise.',
        ],
        list: [
          'Consultez d abord les annonces officielles',
          'Lisez les conditions et la date d expiration',
          'Évitez les sites promettant des remises irréalistes',
          'Combinez codes valides et consolidation',
        ],
      },
    ],
    comparison: {
      title: 'Offre légitime vs. signal d alerte',
      columns: ['Signe', 'Légitime', 'Méfiance'],
      rows: [
        ['Source', 'Canaux officiels', 'Sites tiers inconnus'],
        ['Valeur', 'Modeste, réaliste', 'Réductions énormes'],
        ['Conditions', 'Clairement indiquées', 'Vagues ou absentes'],
        ['Infos personnelles', 'Jamais demandées', 'Demande des données sensibles'],
      ],
    },
    faqs: [
      { q: 'Où trouver des coupons AllChinaBuy ?', a: 'Consultez les annonces officielles, car les offres changent fréquemment.' },
      { q: 'Les codes fonctionnent-ils toujours ?', a: 'Pas toujours. Ils peuvent expirer ou avoir des restrictions de montant ou de catégorie.' },
      { q: 'Comment repérer un faux coupon ?', a: 'Méfiez-vous des remises énormes et des demandes de données personnelles ou de paiement.' },
      { q: 'Quelles autres façons d économiser ?', a: 'La consolidation et le bon choix de ligne d expédition économisent souvent plus.' },
    ],
  },

  'allchinabuy-warehouse': {
    title: 'AllChinaBuy : guide de l entrepôt, stockage et QC',
    shortDescription: 'Ce que fait l entrepôt AllChinaBuy, comment le stockage et les photos QC fonctionnent, et comment vos articles vous parviennent.',
    metaTitle: 'AllChinaBuy : guide de l entrepôt, stockage et QC',
    metaDescription: 'Comprenez le processus d entrepôt AllChinaBuy : stockage, photos QC et préparation de l expédition internationale.',
    h1: 'AllChinaBuy : guide de l entrepôt, stockage et QC',
    intro: 'L entrepôt est l étape intermédiaire entre le vendeur et votre porte. Ce guide explique ce qui se passe à l arrivée, comment le stockage et les photos QC fonctionnent, et comment l entrepôt prépare votre colis.',
    sections: [
      {
        heading: 'Que se passe-t-il à l arrivée',
        paragraphs: [
          'Quand un vendeur expédie votre article, il arrive à l entrepôt et est enregistré dans votre compte. Il est pesé et mesuré, ce qui sert de base à l estimation de livraison.',
          'C est aussi le moment de demander des photos QC pour vérifier l article avant l envoi.',
        ],
      },
      {
        heading: 'Comment fonctionne le stockage',
        paragraphs: [
          'Les entrepôts offrent souvent une période de stockage gratuite, après laquelle des frais peuvent s appliquer. Cela permet de rassembler plusieurs articles avant de les expédier ensemble.',
          'Vérifiez la politique actuelle de stockage pour connaître la durée gratuite.',
        ],
      },
      {
        heading: 'Photos QC et inspections',
        paragraphs: [
          'Les photos QC montrent l article réel avant l expédition. Un jeu de base peut montrer plusieurs angles, tandis que des inspections détaillées vérifient mesures, étiquettes ou détails précis.',
          'Si quelque chose semble incorrect, vous avez plus de chances de le résoudre avant le départ du colis.',
        ],
      },
      {
        heading: 'Préparer votre colis pour l expédition',
        paragraphs: [
          'Une fois prêt, l entrepôt regroupe vos articles, les emballe et les expédie selon la méthode choisie. Retirer les emballages superflus réduit taille et coût.',
        ],
        list: [
          'Les articles sont enregistrés, pesés et mesurés',
          'Les photos QC permettent de vérifier avant l envoi',
          'Une période gratuite aide à consolider',
          'La consolidation réduit le coût total',
        ],
      },
    ],
    comparison: {
      title: 'Étape d entrepôt et ce qu elle implique',
      columns: ['Étape', 'Ce qui se passe', 'Votre action'],
      rows: [
        ['Arrivée', 'Article enregistré et pesé', 'Le vérifier dans le compte'],
        ['Stockage', 'Article conservé', 'Consolider avec d autres commandes'],
        ['QC', 'Photos de l article', 'Demander et vérifier'],
        ['Emballage', 'Articles regroupés', 'Confirmer la méthode'],
      ],
    },
    faqs: [
      { q: 'Qu est-ce que l entrepôt AllChinaBuy ?', a: 'L installation où vos articles sont conservés, pesés et préparés pour l expédition après achat.' },
      { q: 'Le stockage est-il gratuit ?', a: 'Souvent une période gratuite, puis des frais peuvent s appliquer. Vérifiez la politique actuelle.' },
      { q: 'Comment fonctionnent les photos QC ?', a: 'Vous demandez des photos de l article à l entrepôt pour vérifier sa conformité avant l envoi.' },
      { q: 'Qu est-ce que la consolidation ?', a: 'Regrouper plusieurs articles en un colis pour réduire le nombre d envois et le coût.' },
    ],
  },

  'allchinabuy-legit': {
    title: 'AllChinaBuy est-il fiable ? Sécurité et avis',
    shortDescription: 'Un regard indépendant sur la fiabilité et la sécurité d AllChinaBuy, avec des conseils pratiques pour acheter sereinement.',
    metaTitle: 'AllChinaBuy est-il fiable ? Sécurité et avis',
    metaDescription: 'AllChinaBuy est-il fiable ? Un guide indépendant sur sa légitimité, les inquiétudes courantes et des conseils de sécurité pratiques.',
    h1: 'AllChinaBuy est-il fiable ? Sécurité et avis',
    intro: 'Si vous découvrez les agents d achat, il est normal de se demander si un service est fiable et sûr avant d envoyer de l argent. Ce guide donne un point de vue équilibré et indépendant.',
    sections: [
      {
        heading: 'Ce que fiable signifie pour un agent',
        paragraphs: [
          'Un agent d achat est un vrai service qui achète pour vous et expédie à l international. Être fiable signifie généralement tenir ses promesses, communiquer clairement et gérer les problèmes.',
          'L essentiel est d évaluer le service avec des attentes réalistes et la même prudence que pour tout achat en ligne.',
        ],
      },
      {
        heading: 'Inquiétudes courantes et solutions',
        paragraphs: [
          'Les inquiétudes portent surtout sur les délais, la communication et le coût de livraison. Beaucoup se gèrent en lisant les conditions, en utilisant les photos QC et en gardant ses informations organisées.',
          'En cas de doute, demander directement à l agent avant de commander est le plus rapide.',
        ],
      },
      {
        heading: 'Conseils de sécurité pratiques',
        paragraphs: [
          'Payez via les canaux officiels, gardez vos justificatifs et confirmez les détails de livraison. Pour les articles de valeur, envisagez assurance et photos QC.',
          'Ne partagez jamais vos identifiants ou informations de paiement avec quiconque promettant d accélérer ou de réduire votre commande hors du processus officiel.',
        ],
        list: [
          'Payez uniquement via les canaux officiels',
          'Utilisez les photos QC pour vérifier',
          'Gardez vos justificatifs',
          'Envisagez l assurance pour les commandes de valeur',
        ],
      },
      {
        heading: 'Prendre une décision éclairée',
        paragraphs: [
          'Il n y a pas de réponse unique, mais être informé réduit l essentiel du risque. Lisez les retours récents, comprenez les frais et commencez par une petite commande pour gagner en confiance.',
        ],
      },
    ],
    comparison: {
      title: 'Signal de prudence et que faire',
      columns: ['Situation', 'Normal', 'Prudence'],
      rows: [
        ['Paiement', 'Paiement officiel', 'Demandes hors plateforme'],
        ['Contact', 'Support officiel', 'Demande d identifiants'],
        ['Offres', 'Conditions réalistes', 'Garanties irréalistes'],
        ['Livraison', 'Colis suivi', 'Aucun suivi ni info'],
      ],
    },
    faqs: [
      { q: 'AllChinaBuy est-il fiable ?', a: 'C est un vrai service d agent. Vérifiez conditions, frais et retours récents avant de commander.' },
      { q: 'Est-ce sûr d utiliser AllChinaBuy ?', a: 'Paiement officiel, photos QC et justificatifs rendent l expérience plus sûre.' },
      { q: 'À quoi faut-il faire attention ?', a: 'Évitez de payer hors canaux officiels et de partager identifiants ou données de paiement.' },
      { q: 'Commencer par une petite commande ?', a: 'Oui, c est une façon raisonnable de gagner en confiance avant des commandes plus importantes.' },
    ],
  },

  'allchinabuy-qc': {
    title: 'AllChinaBuy : guide QC, lire les photos QC',
    shortDescription: 'Comment fonctionnent les photos QC AllChinaBuy et quoi vérifier avant l expédition.',
    metaTitle: 'AllChinaBuy : guide QC, lire les photos QC',
    metaDescription: 'Apprenez comment fonctionnent les photos QC AllChinaBuy, quoi vérifier et comment demander des inspections utiles.',
    h1: 'AllChinaBuy : guide QC, lire les photos QC',
    intro: 'Les photos QC sont l un des outils les plus précieux quand on achète via un agent, car elles montrent l article réel avant l expédition. Ce guide explique leur fonctionnement et quoi vérifier.',
    sections: [
      {
        heading: 'Ce que sont les photos QC',
        paragraphs: [
          'Les photos QC (quality check) sont prises de votre article après son arrivée à l entrepôt. Elles montrent le produit réel, pas les photos du vendeur, pour repérer les problèmes évidents avant le départ.',
        ],
      },
      {
        heading: 'Quoi vérifier',
        paragraphs: [
          'Concentrez-vous sur les détails qui comptent. Pour les vêtements : taille, couleur, coutures et impressions. Pour les chaussures : forme, logo et couleur. Pour les accessoires : matériaux et finition.',
          'Comparez avec l annonce d origine. Une différence de couleur ou de design mérite d être signalée avant l envoi.',
        ],
        list: [
          'Précision des couleurs et du design',
          'Taille et mesures',
          'Coutures, impressions et finition',
          'Dommages ou défauts',
        ],
      },
      {
        heading: 'Comment demander des photos utiles',
        paragraphs: [
          'Soyez précis en demandant les angles ou détails qui vous importent, comme un gros plan d un logo ou l intérieur d une chaussure.',
          'Si quelque chose semble incorrect, contactez l agent pendant que l article est encore à l entrepôt.',
        ],
      },
      {
        heading: 'Utiliser les photos QC pour décider',
        paragraphs: [
          'Les photos QC aident à repérer les problèmes tôt, mais ce sont des photos, pas une inspection physique. Gardez des attentes réalistes sur leurs limites.',
        ],
      },
    ],
    comparison: {
      title: 'À vérifier selon le type d article',
      columns: ['Article', 'Détails clés', 'Problèmes courants'],
      rows: [
        ['Vêtements', 'Taille, couleur, coutures', 'Mauvaise taille ou couleur'],
        ['Chaussures', 'Forme, logo, couleur', 'Logo ou couleur différents'],
        ['Accessoires', 'Matériau, finition', 'Finition ou dommages'],
        ['Sacs', 'Forme, quincaillerie', 'Problèmes de quincaillerie'],
      ],
    },
    faqs: [
      { q: 'Que sont les photos QC AllChinaBuy ?', a: 'Des photos de votre article réel prises à l entrepôt pour vérifier sa conformité avant l envoi.' },
      { q: 'Comment demander des photos QC ?', a: 'Demandez-les dans votre compte pendant que l article est à l entrepôt, en précisant les angles souhaités.' },
      { q: 'Que vérifier sur les photos QC ?', a: 'Couleur, taille, coutures, impressions et dommages, en comparant avec l annonce.' },
      { q: 'Et si l article semble incorrect ?', a: 'Contactez l agent tant que l article est à l entrepôt, quand la résolution est la plus facile.' },
    ],
  },
  'allchinabuy-alternatives': {
    title: 'Meilleures alternatives AllChinaBuy : agents comparés',
    shortDescription: 'Une comparaison neutre des meilleures alternatives à AllChinaBuy pour choisir le bon agent d achat.',
    metaTitle: 'Meilleures alternatives AllChinaBuy : agents comparés',
    metaDescription: 'À la recherche d alternatives à AllChinaBuy ? Comparez les agents d achat populaires sur la découverte de produits, la livraison, l entrepôt et l expérience débutant.',
    h1: 'Meilleures alternatives AllChinaBuy : agents comparés',
    intro: 'AllChinaBuy est l une des nombreuses options d agents d achat qui aident les acheteurs internationaux à commander sur les marketplaces chinoises et à expédier à l étranger. Ce guide compare quelques alternatives connues de façon neutre, pour vous aider à choisir selon vos priorités.',
    sections: [
      {
        heading: 'Pourquoi chercher des alternatives',
        paragraphs: [
          'Les gens explorent les alternatives pour différentes raisons : certains préfèrent une autre structure de frais, d autres une ligne de livraison précise ou une interface plus simple, et d autres aiment simplement comparer avant de décider.',
          'Comme les agents varient dans leur gestion des frais, du stockage, des photos QC et du support, il n y a pas de meilleur choix universel. Le bon agent dépend de votre pays, de vos achats et de votre niveau d implication.',
        ],
      },
      {
        heading: 'Que comparer entre les agents',
        paragraphs: [
          'Concentrez-vous sur ce qui affecte directement votre coût total et votre expérience. Les frais de service, les options de livraison, les conditions de stockage et la disponibilité des photos QC sont les points de différence les plus courants.',
          'L expérience débutant compte aussi. Une interface claire et un support réactif vous font gagner beaucoup de temps, surtout pour vos premières commandes.',
        ],
        list: [
          'Frais de service et de manutention',
          'Lignes et destinations de livraison',
          'Stockage et consolidation en entrepôt',
          'Options de photos QC et d inspection',
          'Qualité du support client',
        ],
      },
      {
        heading: 'Un regard neutre sur les options populaires',
        paragraphs: [
          'Plusieurs agents se sont forgé une réputation, chacun avec ses points forts. Plutôt que de les classer, il est plus utile de vérifier les conditions actuelles de quelques services et de voir lequel correspond à vos commandes habituelles.',
          'Recherchez des tarifs transparents, des infos de livraison claires et des retours récents d acheteurs de votre pays, qui donnent l image la plus réaliste.',
        ],
      },
      {
        heading: 'Comment faire le bon choix',
        paragraphs: [
          'Commencez par une petite commande pour tester un nouvel agent, afin d évaluer l expérience sans gros engagement. Comparez le coût total livré, frais et livraison inclus, plutôt que le seul prix de l article.',
        ],
        list: [
          'Comparez le coût total livré',
          'Vérifiez les options de livraison pour votre destination',
          'Lisez les retours récents de votre pays',
          'Commencez par une petite commande',
        ],
      },
    ],
    comparison: {
      title: 'Que vérifier en comparant les agents',
      columns: ['Facteur', 'Pourquoi ça compte', 'À vérifier'],
      rows: [
        ['Frais de service', 'Affecte le coût total', 'Vérifiez les frais actuels'],
        ['Livraison', 'Grande part du coût', 'Comparez les lignes vers votre pays'],
        ['Stockage', 'Permet de consolider', 'Confirmez la période gratuite'],
        ['Photos QC', 'Aide à vérifier', 'Vérifiez disponibilité et coût'],
        ['Support', 'Gagne du temps', 'Cherchez des retours récents'],
      ],
    },
    faqs: [
      { q: 'Quelles sont les meilleures alternatives à AllChinaBuy ?', a: 'Plusieurs agents offrent des services similaires. Le meilleur dépend de votre destination, de vos achats et de vos préférences en matière de frais et de livraison.' },
      { q: 'Comment comparer les agents d achat ?', a: 'Comparez les frais de service, les lignes de livraison, les conditions de stockage, les photos QC et les retours récents avant de choisir.' },
      { q: 'Existe-t-il un seul meilleur agent ?', a: 'Non, aucun agent n est le meilleur pour tout le monde. Le bon choix dépend de votre pays, de vos commandes et de votre implication.' },
      { q: 'Faut-il tester avec une petite commande ?', a: 'Oui, commencer petit est une façon sensée d évaluer un nouvel agent avant de s engager sur de gros achats.' },
    ],
  },

  'allchinabuy-comparison': {
    title: 'Guide de comparaison AllChinaBuy : comment il se positionne',
    shortDescription: 'Un guide de comparaison neutre d AllChinaBuy sur les fonctionnalités, le flux et ce qu il faut considérer.',
    metaTitle: 'Guide de comparaison AllChinaBuy : comment il se positionne',
    metaDescription: 'Un guide de comparaison neutre d AllChinaBuy sur la découverte de produits, le flux, l entrepôt, le QC et la livraison pour choisir en connaissance de cause.',
    h1: 'Guide de comparaison AllChinaBuy : comment il se positionne',
    intro: 'Comparer les agents d achat peut sembler accablant car les services se ressemblent au premier abord. Ce guide décompose la comparaison sur les fonctionnalités qui comptent, pour évaluer AllChinaBuy face aux autres options avec lucidité.',
    sections: [
      {
        heading: 'Le flux d achat essentiel',
        paragraphs: [
          'La plupart des agents, dont AllChinaBuy, suivent le même flux : vous ajoutez un lien produit, l agent achète, l article arrive en entrepôt, puis vous choisissez une méthode de livraison.',
          'Les agents diffèrent sur les détails de ce flux : structure des frais, fonctionnement du stockage et contrôle sur le QC et la consolidation.',
        ],
      },
      {
        heading: 'Découverte et recherche de produits',
        paragraphs: [
          'Un bon agent facilite la recherche et le suivi des produits. Des ressources structurées comme un tableur vous aident à comparer articles, prix et vendeurs au même endroit.',
          'C est là que l organisation paie : plus vous voyez clairement vos options, meilleures sont vos décisions.',
        ],
      },
      {
        heading: 'Entrepôt, QC et livraison',
        paragraphs: [
          'L entrepôt est l endroit où vos articles sont stockés, pesés et photographiés avant l envoi. Les photos QC permettent de vérifier un article, tandis que la consolidation aide à combiner plusieurs commandes.',
          'La livraison est souvent le coût variable le plus important, donc comprendre les lignes disponibles et le calcul du poids est essentiel.',
        ],
      },
      {
        heading: 'Faire une comparaison équilibrée',
        paragraphs: [
          'Plutôt que de chercher un gagnant unique, listez les fonctionnalités qui comptent et vérifiez chaque agent. Des tarifs transparents, un support clair et des retours récents sont plus utiles que les arguments marketing.',
        ],
      },
    ],
    comparison: {
      title: 'Aperçu de comparaison des fonctionnalités',
      columns: ['Fonctionnalité', 'AllChinaBuy', 'Autres agents'],
      rows: [
        ['Découverte de produits', 'Prise en charge', 'Généralement disponible'],
        ['Ressources tableur', 'Courant', 'Variable'],
        ['Service d entrepôt', 'Disponible', 'Généralement disponible'],
        ['Photos QC', 'Disponible', 'Variable selon l agent'],
        ['Livraison internationale', 'Disponible', 'Généralement disponible'],
        ['Accompagnement débutant', 'Disponible', 'Variable selon l agent'],
      ],
    },
    faqs: [
      { q: 'Comment AllChinaBuy se compare-t-il aux autres agents ?', a: 'Le flux de base est similaire. Les différences apparaissent dans les frais, les options de livraison, le stockage et les photos QC.' },
      { q: 'Quelles fonctionnalités comparer en premier ?', a: 'Commencez par les frais, les lignes de livraison, les conditions de stockage et les photos QC, qui affectent le plus votre coût.' },
      { q: 'La comparaison est-elle nécessaire avant d acheter ?', a: 'Non obligatoire, mais comparer quelques agents évite les surprises et aide à trouver le bon choix.' },
      { q: 'Quel est le facteur le plus important ?', a: 'Pour la plupart, le coût total livré (article plus frais et livraison) est le plus important, suivi de la fiabilité et du support.' },
    ],
  },

  'allchinabuy-discount-code': {
    title: 'Guide des codes promo AllChinaBuy : économiser plus',
    shortDescription: 'Comment fonctionnent les codes promo AllChinaBuy, où les trouver et comment éviter les offres expirées ou fausses.',
    metaTitle: 'Guide des codes promo AllChinaBuy : économiser plus',
    metaDescription: 'Découvrez comment fonctionnent les codes promo AllChinaBuy, où trouver des offres légitimes et comment repérer les codes expirés ou faux.',
    h1: 'Guide des codes promo AllChinaBuy : économiser plus',
    intro: 'Les codes promo peuvent réduire un peu votre commande, mais seulement si vous trouvez des codes légitimes et les appliquez correctement. Ce guide explique comment ils fonctionnent, d où viennent les offres et comment distinguer une vraie remise d une perte de temps.',
    sections: [
      {
        heading: 'Comment fonctionnent les codes promo',
        paragraphs: [
          'Un code promo est généralement une courte chaîne saisie au moment du paiement pour réduire un frais ou obtenir un pourcentage sur une partie de la commande. Les codes visent souvent les frais de livraison, de service ou certaines catégories.',
          'La plupart des codes ont des conditions, comme un montant minimum, une ligne précise ou une date d expiration, donc lisez toujours les termes.',
        ],
      },
      {
        heading: 'Où trouver des codes légitimes',
        paragraphs: [
          'L endroit le plus fiable est les annonces officielles et les campagnes promotionnelles de la plateforme. Les ventes saisonnières et les newsletters sont d autres sources courantes.',
          'Méfiez-vous des sites tiers, dont les codes sont souvent obsolètes ou inexacts.',
        ],
      },
      {
        heading: 'Repérer un code faux ou expiré',
        paragraphs: [
          'Les codes faux ou expirés promettent souvent des économies irréalistes ou viennent de sites qui demandent des infos personnelles. Si une offre semble trop belle, elle l est probablement.',
          'Un code légitime n exige jamais de partager vos identifiants ou infos de paiement hors du paiement officiel.',
        ],
        list: [
          'Vérifiez d abord les canaux officiels',
          'Lisez les termes, l expiration et le minimum',
          'Évitez les sites promettant des remises énormes',
          'Ne partagez jamais d infos sensibles pour un code',
        ],
      },
      {
        heading: 'Cumuler les économies intelligemment',
        paragraphs: [
          'Au-delà des codes, la consolidation et le bon choix de ligne de livraison économisent souvent plus qu une petite remise. Combiner un code valide avec de bons choix de livraison donne le meilleur résultat.',
        ],
      },
    ],
    comparison: {
      title: 'Code légitime vs signal d alerte',
      columns: ['Signe', 'Légitime', 'Attention'],
      rows: [
        ['Source', 'Canaux officiels', 'Sites tiers inconnus'],
        ['Valeur', 'Modeste, réaliste', 'Énorme, illimitée'],
        ['Termes', 'Clairement indiqués', 'Vagues ou absents'],
        ['Infos', 'Aucune donnée sensible', 'Demande identifiants ou paiement'],
      ],
    },
    faqs: [
      { q: 'Où trouver des codes promo AllChinaBuy ?', a: 'Consultez les annonces officielles et les campagnes promotionnelles, car les offres changent souvent et y sont les plus fiables.' },
      { q: 'Les codes promo expirent-ils ?', a: 'Oui, la plupart ont une date d expiration et peuvent exiger un minimum ou s appliquer à des services précis.' },
      { q: 'Comment savoir si un code est faux ?', a: 'Méfiez-vous des remises énormes ou illimitées et de tout site demandant des infos personnelles ou de paiement.' },
      { q: 'Puis-je cumuler un code avec d autres économies ?', a: 'Cela dépend des termes. Même sans cumul, vous pouvez économiser en consolidant et en choisissant la bonne ligne.' },
    ],
  },

  'allchinabuy-buying-guide': {
    title: 'Comment acheter sur AllChinaBuy : guide complet',
    shortDescription: 'Un guide pas à pas pour acheter sur AllChinaBuy, de la recherche du produit à la réception du colis.',
    metaTitle: 'Comment acheter sur AllChinaBuy : guide complet',
    metaDescription: 'Apprenez à acheter sur AllChinaBuy pas à pas — trouver des produits, ajouter des liens, commander, QC, consolidation et livraison — dans ce guide complet pour débutants.',
    h1: 'Comment acheter sur AllChinaBuy : guide complet',
    intro: 'Si vous n avez jamais utilisé un agent d achat, le processus peut sembler déroutant. Ce guide vous accompagne à travers tout le parcours en étapes claires, de la recherche du produit à l ouverture du colis.',
    sections: [
      {
        heading: 'Étape 1 : trouver le produit',
        paragraphs: [
          'Commencez par parcourir les marketplaces ou catégories qui vous intéressent. Quand vous trouvez un article, copiez le lien direct — c est ce que vous partagerez avec l agent.',
          'Faites attention aux détails de l annonce, tailles, couleurs et mesures, pour bien choisir et réduire les risques de retour.',
        ],
      },
      {
        heading: 'Étape 2 : ajouter le lien et commander',
        paragraphs: [
          'Collez le lien dans votre compte et saisissez les détails voulus, taille et couleur. L agent achètera ensuite l article pour vous.',
          'Relisez attentivement votre commande avant de confirmer, car modifier après achat est plus lent et compliqué.',
        ],
      },
      {
        heading: 'Étape 3 : entrepôt et QC',
        paragraphs: [
          'Une fois l article envoyé à l agent, il arrive à l entrepôt et est enregistré dans votre compte. C est le moment idéal pour demander des photos QC et vérifier l article.',
          'Si quelque chose semble incorrect, contactez l agent tant que l article est encore à l entrepôt.',
        ],
      },
      {
        heading: 'Étape 4 : consolider et expédier',
        paragraphs: [
          'Quand vous êtes prêt, choisissez une méthode de livraison et confirmez votre adresse. Consolider plusieurs articles en un colis réduit le coût total.',
          'Gardez votre numéro de suivi et suivez le colis jusqu à l arrivée, en répondant rapidement aux avis de douane ou de livraison.',
        ],
        list: [
          'Copiez le lien direct du produit',
          'Ajoutez le lien et confirmez taille et couleur',
          'Demandez des photos QC à l entrepôt',
          'Consolidez les articles et choisissez une ligne',
        ],
      },
    ],
    comparison: {
      title: 'Étape d achat vs action',
      columns: ['Étape', 'Action', 'Pourquoi ça compte'],
      rows: [
        ['Trouver', 'Copier le lien', 'Assure le bon article'],
        ['Commander', 'Confirmer taille', 'Réduit les retours'],
        ['QC', 'Demander des photos', 'Vérifie avant l envoi'],
        ['Expédier', 'Consolider et confirmer', 'Réduit le coût et les erreurs'],
      ],
    },
    faqs: [
      { q: 'Comment acheter sur AllChinaBuy ?', a: 'Copiez un lien, ajoutez-le avec votre taille et couleur, laissez l agent acheter, puis demandez le QC et choisissez la livraison.' },
      { q: 'Faut-il connaître le chinois ?', a: 'Non. L agent gère la communication avec les vendeurs, l un des principaux avantages d un agent d achat.' },
      { q: 'Et si l article arrive incorrect ?', a: 'Les photos QC vous permettent de repérer les problèmes tant que l article est à l entrepôt.' },
      { q: 'Puis-je combiner plusieurs commandes ?', a: 'Oui, consolider plusieurs articles en un colis est une façon courante de réduire le coût de livraison.' },
    ],
  },

  'allchinabuy-sellers': {
    title: 'Guide des vendeurs AllChinaBuy : trouver des vendeurs fiables',
    shortDescription: 'Comment rechercher et comparer les vendeurs AllChinaBuy pour commander avec plus de confiance.',
    metaTitle: 'Guide des vendeurs AllChinaBuy : trouver des vendeurs fiables',
    metaDescription: 'Apprenez à rechercher les vendeurs AllChinaBuy, comparer les retours et vérifier les annonces pour choisir des vendeurs fiables et éviter les erreurs.',
    h1: 'Guide des vendeurs AllChinaBuy : trouver des vendeurs fiables',
    intro: 'La qualité de votre expérience dépend souvent des vendeurs que vous choisissez. Ce guide explique comment rechercher les vendeurs, quoi regarder dans leurs annonces et comment comparer pour commander avec plus de confiance.',
    sections: [
      {
        heading: 'Pourquoi la recherche de vendeurs compte',
        paragraphs: [
          'Les vendeurs varient en qualité, communication et fiabilité. Prendre quelques minutes pour rechercher avant de commander évite les articles différents de l annonce ou les vendeurs qui répondent lentement.',
          'Même avec un agent, vous bénéficiez de choisir des vendeurs aux annonces claires et aux retours constants.',
        ],
      },
      {
        heading: 'Que regarder dans une annonce',
        paragraphs: [
          'Une bonne annonce a des photos claires, des mesures détaillées et des descriptions précises des matières et couleurs. Les annonces vagues ou copiées sont souvent un signe de prudence.',
          'Vérifiez si l annonce montre de vraies photos plutôt que des images de stock, et cherchez des guides de tailles.',
        ],
        list: [
          'Photos réelles et claires',
          'Mesures et guides de tailles détaillés',
          'Descriptions précises des matières',
          'Retours récents constants',
        ],
      },
      {
        heading: 'Comparer les vendeurs',
        paragraphs: [
          'Quand deux vendeurs offrent des articles similaires, comparez leurs retours, leur réactivité et le détail des annonces. Un prix un peu plus élevé chez un vendeur plus réactif vaut souvent le coup.',
          'Gardez votre recherche organisée dans un tableur pour comparer sans perdre le fil.',
        ],
      },
      {
        heading: 'Utiliser les photos QC pour vérifier',
        paragraphs: [
          'Les photos QC sont votre meilleur outil pour vérifier que ce qu un vendeur envoie correspond à l annonce. Demandez des angles précis et comparez avec l annonce d origine.',
        ],
      },
    ],
    comparison: {
      title: 'Signal de vendeur vs ce qu il indique',
      columns: ['Signal', 'Positif', 'Prudence'],
      rows: [
        ['Photos', 'Réelles et claires', 'Seulement stock'],
        ['Annonce', 'Détaillée', 'Vague ou copiée'],
        ['Retours', 'Constantes', 'Rares ou contradictoires'],
        ['Réponse', 'Rapide et claire', 'Lente ou évasive'],
      ],
    },
    faqs: [
      { q: 'Comment trouver des vendeurs AllChinaBuy fiables ?', a: 'Cherchez des vendeurs aux photos réelles claires, aux annonces détaillées et aux retours constants, et comparez avant de commander.' },
      { q: 'Que vérifier dans une annonce ?', a: 'Vérifiez les photos réelles, les mesures détaillées, les descriptions de matières et un guide de tailles le cas échéant.' },
      { q: 'Comment les photos QC aident-elles avec les vendeurs ?', a: 'Elles permettent de vérifier que l article envoyé correspond à l annonce, utile pour repérer les écarts.' },
      { q: 'Un prix plus élevé vaut-il un meilleur vendeur ?', a: 'Souvent oui. Un vendeur plus réactif et fiable vous fait gagner du temps et évite des problèmes.' },
    ],
  },

  'allchinabuy-products': {
    title: 'Guide de recherche de produits AllChinaBuy : trouver les meilleurs articles',
    shortDescription: 'Comment rechercher les produits AllChinaBuy, comparer les options et trouver les articles qui vous conviennent.',
    metaTitle: 'Guide de recherche de produits AllChinaBuy',
    metaDescription: 'Apprenez à rechercher efficacement les produits AllChinaBuy — comparer les options, vérifier les détails et organiser votre recherche pour trouver les meilleurs articles.',
    h1: 'Guide de recherche de produits AllChinaBuy : trouver les meilleurs articles',
    intro: 'Avec autant de produits disponibles, une bonne recherche fait la différence entre un excellent achat et une déception. Ce guide explique comment rechercher, comparer et organiser ce que vous trouvez.',
    sections: [
      {
        heading: 'Commencez avec une idée claire',
        paragraphs: [
          'Avant de chercher, définissez ce dont vous avez besoin : style, taille, budget et fonctionnalités indispensables. Un point de départ clair aide à filtrer le grand nombre de résultats.',
          'Notez vos critères clés, puis utilisez-les pour évaluer chaque produit.',
        ],
      },
      {
        heading: 'Comparer les produits similaires',
        paragraphs: [
          'Quand vous trouvez plusieurs candidats, comparez-les sur les détails qui comptent : matières, mesures, prix et retours des vendeurs. Un tableur rend cela bien plus facile.',
          'Concentrez-vous sur les différences qui affectent votre satisfaction, comme la taille, la couleur et la qualité, plutôt que les variations cosmétiques.',
        ],
      },
      {
        heading: 'Vérifier les détails importants',
        paragraphs: [
          'Relisez toujours les mesures, les matières et le nombre de photos réelles. Pour les vêtements, un guide de tailles est essentiel. Pour les chaussures, vérifiez les notes de taille.',
        ],
        list: [
          'Mesures et guides de tailles',
          'Matières et construction',
          'Photos réelles du produit',
          'Retours et réactivité du vendeur',
        ],
      },
      {
        heading: 'Organiser votre recherche',
        paragraphs: [
          'Gardez votre recherche dans une liste structurée avec des colonnes pour le produit, le lien, le prix, le vendeur et les notes. Cela garde tout au même endroit.',
        ],
      },
    ],
    comparison: {
      title: 'Facteur de recherche vs à vérifier',
      columns: ['Facteur', 'À vérifier', 'Pourquoi ça compte'],
      rows: [
        ['Taille', 'Guide des tailles', 'Évite une mauvaise coupe'],
        ['Matières', 'Description', 'Fixe les attentes'],
        ['Photos', 'Images réelles', 'Montre le vrai produit'],
        ['Vendeur', 'Retours', 'Indique la fiabilité'],
        ['Prix', 'Coût total', 'Garde le budget'],
      ],
    },
    faqs: [
      { q: 'Comment rechercher les produits AllChinaBuy ?', a: 'Définissez vos critères, comparez les articles sur les détails et les retours, et organisez tout dans une liste structurée.' },
      { q: 'Que vérifier avant de commander ?', a: 'Vérifiez les mesures, les matières, les photos réelles et les retours, et confirmez le coût total livraison incluse.' },
      { q: 'Comment comparer des produits similaires ?', a: 'Placez-les côte à côte sur les détails qui comptent : taille, matières, prix et fiabilité du vendeur.' },
      { q: 'Pourquoi utiliser un tableur ?', a: 'Un tableur garde liens, prix, vendeurs et notes au même endroit, ce qui facilite la comparaison et les décisions.' },
    ],
  },

  'allchinabuy-safety': {
    title: 'Guide de sécurité AllChinaBuy : acheter en confiance',
    shortDescription: 'Conseils pratiques de sécurité pour acheter via AllChinaBuy, des paiements aux informations personnelles.',
    metaTitle: 'Guide de sécurité AllChinaBuy : acheter en confiance',
    metaDescription: 'Un guide pratique de sécurité AllChinaBuy sur les paiements, les photos QC, la sécurité du compte et les informations personnelles.',
    h1: 'Guide de sécurité AllChinaBuy : acheter en confiance',
    intro: 'Acheter via un agent est généralement sûr quand vous suivez quelques pratiques sensées. Ce guide couvre les bases — paiements, QC, sécurité du compte et informations personnelles — pour vous concentrer sur vos achats.',
    sections: [
      {
        heading: 'Payez uniquement par les canaux officiels',
        paragraphs: [
          'Effectuez toujours vos paiements via le paiement officiel. Évitez d envoyer de l argent directement à des particuliers qui prétendent traiter votre commande, car cela supprime votre protection.',
          'Gardez des traces de vos paiements et commandes, utiles en cas de litige.',
        ],
      },
      {
        heading: 'Utilisez les photos QC pour vérifier',
        paragraphs: [
          'Les photos QC sont l un des outils de sécurité les plus puissants, car elles montrent l article réel avant l envoi. Demandez des angles précis et relisez-les soigneusement avant de confirmer.',
          'Repérer un problème à l entrepôt est bien plus facile que de le résoudre après un envoi international.',
        ],
      },
      {
        heading: 'Protégez votre compte et vos informations',
        paragraphs: [
          'Utilisez un mot de passe fort et unique, et ne partagez jamais vos identifiants. Méfiez-vous des messages ou sites demandant vos infos hors du processus officiel.',
          'Ne saisissez jamais vos données de paiement sur des sites tiers promettant des remises ou une livraison plus rapide.',
        ],
        list: [
          'Payez uniquement via le paiement officiel',
          'Utilisez les photos QC pour vérifier',
          'Utilisez un mot de passe fort et unique',
          'Ne partagez jamais identifiants ou paiement',
          'Évitez les sites tiers demandant des infos',
        ],
      },
      {
        heading: 'Comprendre les risques de livraison et de douane',
        paragraphs: [
          'La livraison internationale comporte toujours des risques, dont les retards et les frais de douane. Comprendre les règles douanières de votre pays et choisir une assurance aide à anticiper.',
        ],
      },
    ],
    comparison: {
      title: 'Pratique de sécurité vs protection',
      columns: ['Pratique', 'Ce qu elle protège', 'Risque évité'],
      rows: [
        ['Paiements officiels', 'Votre argent', 'Fraude et litiges'],
        ['Photos QC', 'Conformité', 'Articles erronés'],
        ['Mot de passe fort', 'Votre compte', 'Accès non autorisé'],
        ['Aucune info tierce', 'Données personnelles', 'Hameçonnage'],
      ],
    },
    faqs: [
      { q: 'AllChinaBuy est-il sûr ?', a: 'Oui, en suivant des pratiques de base comme payer via les canaux officiels, utiliser les photos QC et protéger vos informations.' },
      { q: 'Comment sécuriser mes paiements ?', a: 'Payez toujours via le paiement officiel et évitez d envoyer de l argent à des particuliers.' },
      { q: 'Que faire si un article semble incorrect ?', a: 'Utilisez les photos QC pour repérer les problèmes tant que l article est à l entrepôt.' },
      { q: 'Faut-il se méfier des sites tiers ?', a: 'Oui. Ne saisissez jamais vos données de paiement ou identifiants sur des sites tiers.' },
    ],
  },

  'allchinabuy-tutorial': {
    title: 'Tutoriel débutant AllChinaBuy : votre première commande simplifiée',
    shortDescription: 'Un tutoriel convivial pour débutants sur AllChinaBuy, pas à pas pour votre première commande.',
    metaTitle: 'Tutoriel débutant AllChinaBuy : première commande simplifiée',
    metaDescription: 'Un tutoriel AllChinaBuy pour débutants qui vous accompagne pas à pas dans votre première commande — du produit à la réception — en étapes simples.',
    h1: 'Tutoriel débutant AllChinaBuy : votre première commande simplifiée',
    intro: 'Commencer avec un agent d achat peut intimider, mais cela devient simple une fois que vous comprenez les étapes de base. Ce tutoriel vous accompagne dans votre première commande en langage simple.',
    sections: [
      {
        heading: 'Ce qu il vous faut avant de commencer',
        paragraphs: [
          'Vous n avez pas besoin de grand-chose : un produit, son lien direct et un moyen de payer. Avoir votre adresse de livraison et vos préférences de taille prêtes aide aussi.',
          'Avoir des attentes réalistes est utile : la livraison prend du temps, et le coût total inclut les frais et la livraison.',
        ],
      },
      {
        heading: 'Passer votre toute première commande',
        paragraphs: [
          'Copiez le lien et collez-le dans votre compte, puis choisissez taille et couleur. Relisez tout avant de confirmer, car modifier plus tard est plus lent.',
          'Après l achat par l agent, l article est envoyé à l entrepôt et enregistré dans votre compte.',
        ],
      },
      {
        heading: 'À l entrepôt : QC et stockage',
        paragraphs: [
          'Quand votre article arrive à l entrepôt, vous pouvez demander des photos QC pour vérifier. Vous pouvez aussi attendre et combiner plusieurs articles avant l envoi, ce qui économise souvent de l argent.',
          'Si quelque chose semble incorrect, contactez l agent tant que l article est à l entrepôt.',
        ],
      },
      {
        heading: 'Expédier et recevoir votre colis',
        paragraphs: [
          'Choisissez une méthode de livraison, confirmez votre adresse et payez les frais. Vous recevrez un numéro de suivi pour suivre le colis jusqu à votre porte.',
        ],
        list: [
          'Copiez le lien du produit',
          'Choisissez taille et couleur, puis confirmez',
          'Demandez des photos QC à l entrepôt',
          'Choisissez la livraison et suivez le colis',
        ],
      },
    ],
    comparison: {
      title: 'Étape vs ce qui se passe',
      columns: ['Étape', 'Ce qui se passe', 'Votre rôle'],
      rows: [
        ['Commande', 'L agent achète', 'Ajouter le lien et confirmer'],
        ['Entrepôt', 'Article enregistré et pesé', 'Demander les photos QC'],
        ['Consolidation', 'Articles combinés', 'Choisir les articles à combiner'],
        ['Livraison', 'Colis expédié', 'Choisir une ligne et suivre'],
      ],
    },
    faqs: [
      { q: 'AllChinaBuy est-il difficile pour les débutants ?', a: 'Pas vraiment. Le processus est une série d étapes simples — ajouter un lien, confirmer, demander le QC et choisir la livraison — que vous apprenez vite.' },
      { q: 'Que préparer avant ma première commande ?', a: 'Ayez votre lien, votre adresse et vos préférences de taille prêtes, et gardez à l esprit que le coût total inclut les frais et la livraison.' },
      { q: 'Combien de temps dure le processus ?', a: 'Cela dépend du vendeur, de l entrepôt et de la méthode choisie. La livraison internationale peut prendre de quelques jours à quelques semaines.' },
      { q: 'Puis-je demander de l aide pendant ma première commande ?', a: 'Oui, la plupart des agents offrent un support que vous pouvez contacter en cas de doute.' },
    ],
  },

  'allchinabuy-shipping-time': {
    title: 'Délai de livraison AllChinaBuy : à quoi s attendre',
    shortDescription: 'Comprendre le délai de livraison AllChinaBuy, ce qui l affecte et comment estimer votre fenêtre de livraison.',
    metaTitle: 'Délai de livraison AllChinaBuy : à quoi s attendre',
    metaDescription: 'Découvrez ce qui affecte le délai de livraison AllChinaBuy, comment les méthodes se comparent et comment estimer votre fenêtre de livraison.',
    h1: 'Délai de livraison AllChinaBuy : à quoi s attendre',
    intro: 'Le délai de livraison est l une des premières questions des nouveaux acheteurs, et à juste titre. Ce guide explique ce qui influence le délai, comment les méthodes se comparent et comment estimer votre fenêtre de livraison.',
    sections: [
      {
        heading: 'Ce qui affecte le délai de livraison',
        paragraphs: [
          'Plusieurs facteurs déterminent la durée, à commencer par la méthode de livraison. Votre pays de destination, le traitement douanier et la période de l année jouent aussi un rôle.',
          'Les périodes de pointe et les fêtes ajoutent des retards, donc prévoyez du temps supplémentaire pour une date précise.',
        ],
      },
      {
        heading: 'Comment les méthodes se comparent',
        paragraphs: [
          'Les lignes express sont généralement les plus rapides mais coûtent plus cher, tandis que les lignes économiques sont plus lentes mais moins chères. Les lignes standard se situent entre les deux.',
          'Le meilleur choix dépend de votre urgence et de votre budget.',
        ],
      },
      {
        heading: 'Estimer votre fenêtre de livraison',
        paragraphs: [
          'Une estimation réaliste combine le temps de traitement du vendeur, le temps d arrivée à l entrepôt et le transit de la méthode choisie. Vérifier les délais de chaque étape donne une image plus claire.',
          'Utilisez votre numéro de suivi une fois le colis expédié pour la vue la plus à jour.',
        ],
      },
      {
        heading: 'Éviter les retards inutiles',
        paragraphs: [
          'Vérifiez votre adresse, répondez vite aux avis de douane et consolidez les articles tôt. Ces petites étapes aident à faire avancer les choses.',
        ],
        list: [
          'Choisissez une méthode adaptée à votre urgence',
          'Prévoyez du temps autour des fêtes',
          'Vérifiez votre adresse',
          'Suivez le colis et répondez aux avis',
        ],
      },
    ],
    comparison: {
      title: 'Méthode vs vitesse typique',
      columns: ['Méthode', 'Vitesse', 'Coût'],
      rows: [
        ['Express', 'La plus rapide', 'Le plus élevé'],
        ['Standard', 'Équilibrée', 'Modéré'],
        ['Économique', 'La plus lente', 'Le plus bas'],
      ],
    },
    faqs: [
      { q: 'Combien de temps dure la livraison AllChinaBuy ?', a: 'Cela dépend de la méthode et de votre destination. Les lignes express sont rapides, les économiques plus lentes.' },
      { q: 'Qu est-ce qui affecte le délai ?', a: 'La méthode, votre destination, la douane et la période de l année affectent la durée.' },
      { q: 'Comment estimer ma fenêtre de livraison ?', a: 'Combinez le temps du vendeur, de l entrepôt et le transit, et utilisez votre numéro de suivi.' },
      { q: 'Les fêtes causent-elles des retards ?', a: 'Oui, les périodes de pointe ajoutent souvent des retards, donc prévoyez du temps supplémentaire.' },
    ],
  },

  'allchinabuy-shipping-methods': {
    title: 'Méthodes de livraison AllChinaBuy : trouver la bonne ligne',
    shortDescription: 'Un guide des méthodes de livraison AllChinaBuy, leurs différences et comment choisir la bonne ligne.',
    metaTitle: 'Méthodes de livraison AllChinaBuy : trouver la bonne ligne',
    metaDescription: 'Comprenez les méthodes de livraison AllChinaBuy — express, standard et économique — et apprenez à choisir la bonne ligne pour votre colis.',
    h1: 'Méthodes de livraison AllChinaBuy : trouver la bonne ligne',
    intro: 'Choisir la bonne méthode de livraison peut vous faire économiser de l argent ou recevoir votre colis plus vite. Ce guide explique les principaux types de lignes, leurs différences et comment choisir la meilleure.',
    sections: [
      {
        heading: 'Les principaux types de lignes',
        paragraphs: [
          'Les lignes se divisent généralement en trois catégories : express, standard et économique. Express est la plus rapide mais la plus chère, économique la moins chère mais la plus lente, et standard équilibre les deux.',
          'Dans ces catégories, certaines lignes conviennent mieux à certaines destinations ou tailles, donc comparez les options de votre colis.',
        ],
      },
      {
        heading: 'Comment choisir la bonne méthode',
        paragraphs: [
          'Pensez à votre urgence et à votre budget. Sans urgence, une ligne économique peut économiser beaucoup. Si la vitesse compte, express vaut le coût.',
          'Considérez aussi la valeur de l article — pour les articles de valeur, une méthode avec suivi fiable et assurance est plus sûre.',
        ],
        list: [
          'Adaptez la méthode à votre urgence',
          'Équilibrez vitesse et coût',
          'Vérifiez suivi et assurance',
          'Considérez les règles de votre destination',
        ],
      },
      {
        heading: 'Poids, volume et coût',
        paragraphs: [
          'Rappelez-vous que les transporteurs facturent le poids réel ou volumétrique, le plus élevé. Un emballage compact réduit le poids facturé et votre coût.',
        ],
      },
      {
        heading: 'Suivi et assurance par méthode',
        paragraphs: [
          'La plupart des méthodes incluent le suivi, mais le niveau de détail et l assurance varient. Pour les articles de valeur, confirmez que votre ligne supporte l assurance.',
        ],
      },
    ],
    comparison: {
      title: 'Méthode vs caractéristiques',
      columns: ['Méthode', 'Vitesse', 'Coût', 'Idéale pour'],
      rows: [
        ['Express', 'Rapide', 'Élevé', 'Articles urgents ou de valeur'],
        ['Standard', 'Équilibrée', 'Modéré', 'Commandes courantes'],
        ['Économique', 'Lente', 'Bas', 'Commandes non urgentes'],
      ],
    },
    faqs: [
      { q: 'Quelles méthodes de livraison AllChinaBuy offre-t-il ?', a: 'Les lignes se divisent en express, standard et économique, avec des lignes adaptées à certaines destinations.' },
      { q: 'Quelle méthode choisir ?', a: 'Adaptez la méthode à votre urgence et budget : express pour la vitesse, économique pour économiser, standard pour un équilibre.' },
      { q: 'Toutes les méthodes incluent-elles le suivi ?', a: 'La plupart incluent le suivi, mais le détail et l assurance varient, donc confirmez avant d expédier des articles de valeur.' },
      { q: 'L emballage affecte-t-il le coût ?', a: 'Oui, les transporteurs facturent le poids réel ou volumétrique, donc un emballage compact réduit le poids et le coût.' },
    ],
  },
};

export default fr;